<template>
  <Modal v-bind="$attrs" v-on="$listeners" :xIconDisabled="true">
    <!-- 出力中は白く覆う -->
    <div v-if="downloading" class="loading-mask">
      出力中…
    </div>

    <template #title>
      <span class="text-base">Excel出力（{{ def ? def.name : '' }}）</span>
    </template>
    <template #subtitle>
      今の検索条件{{ conditionText ? `（${conditionText}）` : '' }}で、ページに関係なく全件を出します。出す項目を選んでください
    </template>

    <div class="content">
      <div v-if="loading" class="py-8 text-center font-bold text-gray-400">Loading...</div>

      <template v-else-if="def">
        <!-- 全選択・全解除と、選んでいる数 -->
        <div class="flex items-center gap-2 mb-3">
          <button type="button" class="sel-btn" @click="selectAll(true)">全選択</button>
          <button type="button" class="sel-btn" @click="selectAll(false)">全解除</button>
          <span class="ml-2 text-sm font-bold" :class="checkedCount ? 'text-indigo-900' : 'text-red-600'">{{ checkedCount }} / {{ totalCount }} 項目</span>
          <span v-if="!checkedCount" class="text-xs text-red-600 font-bold">1 つ以上選んでください</span>
        </div>

        <!-- グループごとに項目のチェック。グループの見出しのチェックで、そのグループを一度に ON／OFF -->
        <div class="groups">
          <div v-for="g in def.groups" :key="g.key" class="group">
            <label class="group-head">
              <input
                type="checkbox"
                :checked="groupState(g) === 'all'"
                :indeterminate.prop="groupState(g) === 'some'"
                @change="toggleGroup(g, $event.target.checked)"
              />
              {{ g.name }}
            </label>
            <div class="items">
              <label v-for="it in g.items" :key="it.key" class="item">
                <input v-model="checked[it.key]" type="checkbox" />
                {{ it.name }}<span v-if="it.day" class="text-xs text-gray-500">（月〜日）</span>
              </label>
            </div>
          </div>

          <!-- 項目と別に選べる追加のシート（清掃員：家族・緊急連絡先、契約詳細：雇用指示） -->
          <div v-if="def.sheets.length" class="group sheets">
            <div class="group-head">追加のシート</div>
            <div class="items">
              <label v-for="s in def.sheets" :key="s.key" class="item" :title="s.note">
                <input v-model="sheets[s.key]" type="checkbox" />
                {{ s.name }}
                <span class="block text-xs text-gray-500 font-normal">{{ s.note }}</span>
              </label>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="py-8 text-center font-bold text-red-600">項目の定義を取得できませんでした。もう一度開いてください。</div>
    </div>

    <template #action>
      <PrimaryButton text="閉じる" class="w-32 bg-gray-400 mr-2" :disabled="downloading" @click="close" />
      <PrimaryButton :text="downloading ? '出力中…' : 'Excel 出力'" class="w-40" :disabled="downloading || !def || !checkedCount" @click="download" />
    </template>
  </Modal>
</template>

<script>
import Modal from '@components/Modal.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import moment from 'moment'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'

// 選んだ項目を控えるローカルストレージのキー（軸ごと。次に開いたとき同じ状態にする）
// 控えるのは OFF にした項目（項目が増えたとき、新しい項目は ON で出るように）
const STORAGE_PREFIX = 'akbsRegisteredExcel_'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export default {
  components: {
    Modal,
    PrimaryButton
  },

  props: {
    // 開いているか
    open: {
      type: Boolean,
      default: false
    },
    // 軸（'staff'・'employment'・'contract'）
    axis: {
      type: String,
      required: true
    },
    // 今の絞り込み（name・scope）
    filter: {
      type: Object,
      default: () => ({})
    },
    // 検索条件の説明（副題に出す）
    conditionText: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      // 項目の定義（軸ごと。初めて開いたときに取って持ち続ける）
      defs: null,
      loading: false,
      downloading: false,
      // 項目のキー → ON か。キーは定義を取ったときに全部そろえる（後から足すとリアクティブにならない）
      checked: {},
      // 追加のシートのキー → ON か
      sheets: {}
    }
  },

  computed: {
    /**
     * 今の軸の定義（{ name, groups, sheets }）
     */
    def() {
      return this.defs ? this.defs[this.axis] || null : null
    },

    /**
     * 全項目のキー
     */
    allKeys() {
      const keys = []
      for (const g of (this.def ? this.def.groups : [])) {
        for (const it of g.items) {
          keys.push(it.key)
        }
      }
      return keys
    },

    totalCount() {
      return this.allKeys.length
    },

    checkedCount() {
      return this.allKeys.filter(k => this.checked[k]).length
    }
  },

  watch: {
    /**
     * 開いたら定義を取って（初回だけ）、今の軸の選択を控えから戻す
     */
    async open(v) {
      if (!v) {
        return
      }
      if (!this.defs) {
        await this.loadDefs()
      }
      this.initSelection()
    }
  },

  methods: {
    /**
     * 項目の定義を取る
     * API: akbsRegisteredList/getExcelColumns
     */
    async loadDefs() {
      this.loading = true
      try {
        const res = await backend.searchData('akbsRegisteredList/getExcelColumns', {})
        this.defs = res.data ? res.data.data : null
      } catch (error) {
        console.log(error)
        this.defs = null
      } finally {
        this.loading = false
      }
    },

    /**
     * 今の軸の選択を作る（控えがあれば OFF にしていた項目を OFF に。無ければ全部 ON）
     */
    initSelection() {
      const def = this.def
      if (!def) {
        return
      }
      const saved = this.loadSaved()
      const checked = {}
      for (const g of def.groups) {
        for (const it of g.items) {
          checked[it.key] = !saved.off.includes(it.key)
        }
      }
      const sheets = {}
      for (const s of def.sheets) {
        sheets[s.key] = !saved.sheetsOff.includes(s.key)
      }
      this.checked = checked
      this.sheets = sheets
    },

    /**
     * 控えを読む（無ければ空）
     */
    loadSaved() {
      try {
        const item = JSON.parse(localStorage.getItem(STORAGE_PREFIX + this.axis))
        return {
          off: Array.isArray(item && item.off) ? item.off : [],
          sheetsOff: Array.isArray(item && item.sheetsOff) ? item.sheetsOff : []
        }
      } catch (e) {
        return { off: [], sheetsOff: [] }
      }
    },

    /**
     * 今の選択を控える（出力したときだけ）
     */
    save() {
      const off = this.allKeys.filter(k => !this.checked[k])
      const sheetsOff = (this.def ? this.def.sheets : []).map(s => s.key).filter(k => !this.sheets[k])
      try {
        localStorage.setItem(STORAGE_PREFIX + this.axis, JSON.stringify({ off, sheetsOff }))
      } catch (e) {
        console.log(e)
      }
    },

    /**
     * グループの状態（'all'：全部 ON、'some'：一部、'none'：全部 OFF）
     * @param {Object} g グループ
     */
    groupState(g) {
      const on = g.items.filter(it => this.checked[it.key]).length
      if (!on) {
        return 'none'
      }
      return on === g.items.length ? 'all' : 'some'
    },

    /**
     * グループの項目を一度に ON／OFF
     * @param {Object} g グループ
     * @param {Boolean} on ON にするか
     */
    toggleGroup(g, on) {
      for (const it of g.items) {
        this.checked[it.key] = on
      }
    },

    /**
     * 全選択・全解除（項目だけ。追加のシートは変えない）
     * @param {Boolean} on ON にするか
     */
    selectAll(on) {
      for (const k of this.allKeys) {
        this.checked[k] = on
      }
    },

    /**
     * 閉じる（v-model を false に）
     */
    close() {
      this.$emit('input', false)
    },

    /**
     * Excel を出す（今の検索条件＋選んだ項目で全件）。出せたら選択を控えて閉じる
     * API: akbsRegisteredList/downloadExcel
     */
    async download() {
      const def = this.def
      if (!def || !this.checkedCount) {
        return
      }
      this.downloading = true
      try {
        const params = {
          axis: this.axis,
          name: this.filter.name || '',
          scope: this.filter.scope || 'all',
          items: this.allKeys.filter(k => this.checked[k]).join(','),
          sheets: def.sheets.map(s => s.key).filter(k => this.sheets[k]).join(',')
        }
        const res = await backend.getBlob('akbsRegisteredList/downloadExcel', params)
        // 対象の行が無いときは 204（中身なし）
        if (!res || res.status === 204 || !res.data || !res.data.size) {
          await dialogs.showWarningDialog('Excel出力', '対象データがありません。検索条件を変えてください。')
          return
        }
        const blob = new Blob([res.data], { type: XLSX_MIME })
        const a = document.createElement('a')
        a.href = (window.URL || window.webkitURL).createObjectURL(blob)
        // ファイル名は「雇用指示__20261104.xlsx」の形（軸名・アンダースコア 2 つ・日付）
        a.download = `${def.name}__${moment().format('YYYYMMDD')}.xlsx`
        a.click()
        a.remove()
        this.save()
        this.close()
      } catch (error) {
        console.log(error)
        await dialogs.showErrorDialog('Excel出力', 'Excel の出力に失敗しました。もう一度お試しください。')
      } finally {
        this.downloading = false
      }
    }
  }
}
</script>

<style scoped>
.content {
  width: 760px;
  max-width: calc(100vw - 6rem);
}

/* 出力中のマスク（登録済み一覧の保存中と同じ見た目） */
.loading-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  color: #6b7280;
  font-weight: 700;
  font-size: 14px;
}

/* 全選択・全解除 */
.sel-btn {
  padding: 3px 14px;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  background: #fff;
  color: #374151;
  font-size: 0.75rem;
  font-weight: bold;
}

.sel-btn:hover {
  background: #f3f4f6;
}

/* グループ（見出し＋項目。項目は横に並べて折り返す） */
.groups {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.group {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

.group-head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #eef2ff;
  color: #312e81;
  font-size: 0.875rem;
  font-weight: bold;
  cursor: pointer;
}

.items {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  padding: 8px 12px;
}

.item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.875rem;
  color: #1f2937;
  cursor: pointer;
  white-space: nowrap;
}

/* 追加のシートは説明を下に出すので縦に並べる */
.group.sheets .items {
  flex-direction: column;
  align-items: flex-start;
}

.group.sheets .item {
  align-items: flex-start;
  white-space: normal;
  font-weight: bold;
}

.group.sheets .item input {
  margin-top: 3px;
}

/* チェックボックスの色（@tailwindcss/forms なので color で変える） */
input[type='checkbox'] {
  color: #4338ca;
}
</style>
